import React, { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import i18n from '../../i18n';

// Bookmarked/backgrounded tab still has the previous deploy's main.js, which
// maps a lazy route to a chunk hash a newer deploy has already overwritten —
// react-router has no errorElement by default, so that failed dynamic
// import fell through to its own generic "Unexpected Application Error"
// screen (bypassing App.jsx's ErrorBoundary, which only wraps RouterProvider
// and never sees an error handled internally by the router). Reloading once
// picks up the current bundle's correct chunk map.
//
// CHUNK_RELOAD_FLAG stores a TIMESTAMP, not a boolean — a boolean "already
// tried" guard, cleared the instant App.jsx next mounts (i.e. on the very
// reload this triggers, before we know whether it actually fixed anything),
// defeats its own one-reload-only intent: if the reload still serves a
// stale bundle (e.g. a cached index.html, or another deploy landing in the
// gap), the guard is already clear again and it reloads forever — visible
// as a Loading/Updating flicker. A cooldown window is simpler and robust
// either way: it can't loop faster than the cooldown, and it doesn't need
// to know whether any particular reload "succeeded".
export const CHUNK_RELOAD_FLAG = 'iap:chunkReloadAttemptedAt';
const RELOAD_COOLDOWN_MS = 15000;

function isChunkLoadError(error) {
  const message = error?.message || '';
  return error?.name === 'ChunkLoadError'
    || /loading chunk [\w-]+ failed/i.test(message)
    || /dynamically imported module/i.test(message);
}

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (!chunkError) return;
    let lastAttempt = 0;
    try { lastAttempt = Number(sessionStorage.getItem(CHUNK_RELOAD_FLAG)) || 0; } catch { /* ignore */ }
    if (Date.now() - lastAttempt < RELOAD_COOLDOWN_MS) return;
    try { sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now())); } catch { /* ignore */ }
    window.location.reload();
  }, [chunkError]);

  useEffect(() => {
    console.error('Route error:', error);
  }, [error]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg, #f9fafb)', padding: 24,
    }}>
      <div style={{
        maxWidth: 420, textAlign: 'center', background: 'var(--surface, #fff)',
        border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: '32px 28px',
      }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1, #111827)', margin: '0 0 8px' }}>
          {chunkError
            ? i18n.t('common:components.errorBoundary.updatingTitle', 'Updating…')
            : i18n.t('common:components.errorBoundary.title', 'Something went wrong')}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-3, #6b7280)', margin: '0 0 20px' }}>
          {chunkError
            ? i18n.t('common:components.errorBoundary.updatingDescription', "The app was just updated — reloading to get the latest version.")
            : i18n.t('common:components.errorBoundary.description', 'This page hit an unexpected error. Reloading usually fixes it — if it keeps happening, let us know.')}
        </p>
        {!chunkError && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '9px 18px', borderRadius: 8, border: 'none',
              background: 'var(--accent, #10b981)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {i18n.t('common:components.errorBoundary.reload', 'Reload page')}
          </button>
        )}
      </div>
    </div>
  );
}
